import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { useHistoricPayments } from "@/hooks/use-historic-payments";
import { useLegalDocuments } from "@/hooks/use-legal-documents";
import { useOutstandingPayments } from "@/hooks/use-outstanding-payments";
import Header from "@/components/layout/header";
import Sidebar from "@/components/layout/sidebar";
import MobileNav from "@/components/layout/mobile-nav";
import { PageBar } from "@/components/layout/page-bar";
import { PAGE_CONTENT_CLASS } from "@/components/layout/page-shell";
import {
  FiguresStrip,
  FiguresStripSkeleton,
  type Figure,
} from "@/components/layout/figures-strip";
import ClientList from "@/components/dashboard/client-list";
import ProfileCompletionBanner from "@/components/dashboard/profile-completion-banner";
import DocumentsToSignBanner from "@/components/dashboard/documents-to-sign-banner";
import { CONTRACT_DOCS_KEY } from "@/hooks/use-contract-documents";
import ProfileSection from "@/components/dashboard/profile-section";
import { RefreshCw } from "lucide-react";
import { HistoricPayment } from "@shared/schema";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { formatRand, splitRand } from "@/lib/format-money";
import { lazy, Suspense } from "react";

// Lazy so recharts stays out of the entry bundle; fixed-height fallbacks
// match the chart cards to avoid layout shift.
const IncomeChart = lazy(() => import("@/components/dashboard/income-chart"));
const ClientCountChart = lazy(() => import("@/components/dashboard/client-count-chart"));

type DashboardData = {
  clientCount: number;
  monthlyHours: number;
  monthlyIncome: number;
  clients: Array<{
    id: string;
    client_name: string;
    weekly_hours: number;
    income: number;
  }>;
  monthlyIncomeData: Array<{
    date: string;
    amount: number;
  }>;
};

export default function Dashboard() {
  const queryClient = useQueryClient();
  const { user, isLoading: isLoadingAuth } = useAuth();
  const {
    data: historicPaymentsData,
    isLoading: isLoadingHistoricPayments,
    isError: isHistoricPaymentsError,
    isFetching: isFetchingHistoricPayments,
  } = useHistoricPayments(user?.uid);


  const {
    data: dashboardData,
    isLoading,
    error,
    refetch,
    isFetching: isFetchingDashboard,
  } = useQuery<DashboardData>({
    queryKey: ["/api/dashboard", user?.uid],
    enabled: !!user?.uid,
    queryFn: async () => {
      const res = await apiRequest("GET", `/client_summary/pa/${user?.uid}/clients`);
      const data = await res.json();
      
      // Calculate total monthly hours and income from client data
      const totalMonthlyHours = data.clients.reduce((sum: number, client: any) => sum + (client.weekly_hours * 4), 0);
      const totalMonthlyIncome = data.clients.reduce((sum: number, client: any) => sum + client.income, 0);
      
      // Transform the data to match our DashboardData type
      return {
        clientCount: data.clients.length,
        monthlyHours: totalMonthlyHours,
        monthlyIncome: totalMonthlyIncome,
        clients: data.clients.map((client: any, index: number) => ({
          id: `client-${index}`,
          client_name: client.client_name,
          weekly_hours: client.weekly_hours,
          income: client.income,
        })),
        monthlyIncomeData: data.monthlyIncomeData || [],
      };
    },
  });

  // Transform historic payments data to match the format expected by IncomeChart
  const historicIncomeData = historicPaymentsData?.payments.map((payment: HistoricPayment) => ({
    date: `${payment.year}-${payment.month.toString().padStart(2, '0')}`,
    amount: payment.income,
  })) || [];

  // The two metrics that used to be their own cards, read here so they can
  // become cells of the one strip. They are separate queries from the dashboard
  // one, so each cell carries its own loading/error state — the strip renders as
  // soon as the dashboard query lands, and a cell that says "0" before its own
  // query has answered is stating a number it doesn't have.
  const {
    data: legalDocs,
    isLoading: isLoadingLegalDocs,
    isError: isLegalDocsError,
    isFetching: isFetchingLegalDocs,
  } = useLegalDocuments(user?.uid);
  const {
    data: outstanding,
    isLoading: isLoadingOutstanding,
    isError: isOutstandingError,
    isFetching: isFetchingOutstanding,
  } = useOutstandingPayments(user?.uid);

  const signedDocs = (legalDocs ?? []).filter((doc) => !!doc.signed_date).length;
  const totalDocs = legalDocs?.length ?? 0;
  const signedPct = totalDocs ? (signedDocs / totalDocs) * 100 : 0;
  const owed = outstanding?.total_owed_to_sidekick ?? 0;

  const figures: Figure[] = [
    {
      label: "Clients",
      value: dashboardData?.clientCount ?? 0,
      // The signed-agreement count belongs to the legal-docs query. Until that
      // one lands (or if it fails) fall back to the roster line rather than
      // reporting "0 with a signed agreement".
      note:
        !isLoadingLegalDocs && !isLegalDocsError && totalDocs
          ? `${signedDocs} with a signed agreement`
          : "Across your roster",
      pct: 100,
      tone: "blue",
    },
    {
      label: "Hours this month",
      value: Number(dashboardData?.monthlyHours ?? 0).toFixed(1),
      note: "Contracted across all clients",
      pct: 100,
      tone: "emerald",
    },
    {
      label: "Contracted",
      value: splitRand(dashboardData?.monthlyIncome ?? 0).main,
      suffix: splitRand(dashboardData?.monthlyIncome ?? 0).cents,
      note: "Recurring monthly",
      pct: 100,
      tone: "money",
    },
    {
      label: "Outstanding",
      pending: isLoadingOutstanding,
      value: isOutstandingError ? "—" : splitRand(owed).main,
      suffix: isOutstandingError ? undefined : splitRand(owed).cents,
      note: isOutstandingError
        ? "Couldn't load — try Refresh"
        : outstanding && outstanding.count > 0
          ? `${outstanding.count} ${outstanding.count === 1 ? "payment" : "payments"} past due`
          : "All caught up",
      pct: !isOutstandingError && outstanding && outstanding.count > 0 ? 100 : 0,
      tone: !isOutstandingError && outstanding && outstanding.count > 0 ? "red" : "none",
      valueClassName:
        !isOutstandingError && outstanding && outstanding.count === 0
          ? "text-emerald-700 dark:text-emerald-400"
          : undefined,
    },
    {
      label: "Legal docs",
      pending: isLoadingLegalDocs,
      value: isLegalDocsError ? "—" : signedDocs,
      suffix: isLegalDocsError ? undefined : `/${totalDocs}`,
      note: isLegalDocsError
        ? "Couldn't load — try Refresh"
        : totalDocs
          ? `${Math.round(signedPct)}% signed`
          : "Nothing on file yet",
      pct: isLegalDocsError ? 0 : signedPct,
      tone: isLegalDocsError
        ? "none"
        : signedPct >= 100
          ? "emerald"
          : signedPct >= 50
            ? "amber"
            : "red",
    },
  ];

  // Refresh means the whole page, not just the one query that happens to own the
  // button. Previously this refetched only the client-summary query, so a figure
  // or chart stuck on a cached failure stayed stuck no matter how often you
  // pressed it — the only cure was a full reload.
  const handleRefresh = () => {
    refetch();
    for (const key of [
      "historic_payments",
      "legal_documents",
      "outstanding_payments",
    ]) {
      queryClient.invalidateQueries({ queryKey: [key, user?.uid] });
    }
    queryClient.invalidateQueries({ queryKey: [CONTRACT_DOCS_KEY] });
  };

  // The button now drives four queries, so it has to spin until all four settle.
  const isRefreshing =
    isFetchingDashboard ||
    isFetchingHistoricPayments ||
    isFetchingLegalDocs ||
    isFetchingOutstanding;

  return (
    <div className="min-h-screen flex">
      <Sidebar />

      <div className="flex flex-1 flex-col min-w-0">
        <Header />

        <main className="flex-1 overflow-y-auto pb-16 lg:pb-0">
          <div className={PAGE_CONTENT_CLASS}>
            <ProfileCompletionBanner />
            <DocumentsToSignBanner />

            {/* The mobile greeting is a different job from the desktop page bar
                — one welcomes you, the other labels a workspace — so they stay
                two elements rather than one that compromises. */}
            <h1 className="mb-4 text-[25px] font-extrabold leading-[1.15] tracking-[-0.025em] text-foreground lg:hidden">
              Welcome back,<br />
              {user?.name?.split(' ')[0] || 'User'} 👋
            </h1>

            <div className="hidden lg:block">
              <PageBar
                title="Sidekick Companion"
                subtitle={`Welcome back, ${user?.name?.split(' ')[0] || 'User'}`}
              >
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  className="flex h-8 flex-none items-center gap-2 rounded-[9px] text-[12.5px] font-semibold"
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </PageBar>
            </div>

            <div className="mb-3">
              {isLoadingAuth || isLoading ? (
                <FiguresStripSkeleton />
              ) : error ? (
                <div className="w-full rounded-lg bg-red-50 p-4 text-red-500 dark:bg-red-500/10 dark:text-red-400">
                  Failed to load dashboard data
                </div>
              ) : dashboardData ? (
                <FiguresStrip figures={figures} />
              ) : null}
            </div>

            <div className="grid grid-cols-1 items-start gap-3 lg:grid-cols-[1.5fr_1fr]">
              {/* Left column: Income chart → Your clients → Clients over time */}
              <div className="flex min-w-0 flex-col gap-3">
                {isLoadingAuth || isLoadingHistoricPayments ? (
                  <Skeleton className="h-[292px] rounded-lg" />
                ) : isHistoricPaymentsError ? (
                  // An empty chart is a claim that this PA earned nothing. Say
                  // the request failed instead.
                  <div className="flex h-[292px] flex-col items-center justify-center gap-2 rounded-lg border border-border-subtle bg-card p-4 text-center">
                    <p className="text-[13px] font-semibold text-foreground">
                      Couldn't load income history
                    </p>
                    <p className="text-[12px] text-muted-foreground">
                      Press Refresh to try again.
                    </p>
                  </div>
                ) : (
                  <Suspense fallback={<Skeleton className="h-[292px] rounded-lg" />}>
                    <IncomeChart data={historicIncomeData} />
                  </Suspense>
                )}
                {isLoading ? (
                  <Skeleton className="h-[292px] rounded-lg" />
                ) : dashboardData ? (
                  <ClientList />
                ) : null}
                <Suspense fallback={<Skeleton className="h-[292px] rounded-lg" />}>
                  <ClientCountChart uid={user?.uid} />
                </Suspense>
              </div>

              {/* Right column: Profile section spanning full height of the left stack (lg+) */}
              <ProfileSection className="lg:h-full" />
            </div>
          </div>
        </main>
      </div>
      
      <MobileNav />
    </div>
  );
}
